import { Link } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/constants/routes'
import { EXTERNAL_LINKS } from '@/constants/links'
import { useAuth } from '@/contexts/authContext'
import { Logo } from '@/components/Logo'

const FOOTER_SECTIONS = {
  ENTREPRISE: {
    titleKey: 'footer.sections.company',
    links: [
      { labelKey: 'footer.links.discover', path: ROUTES.DISCOVER },
      { labelKey: 'footer.links.subscribe', path: ROUTES.SUBSCRIBE },
    ],
  },
  LEGAL: {
    titleKey: 'footer.sections.legal',
    links: [{ labelKey: 'footer.links.cgu', path: ROUTES.CGU }],
  },
  RESSOURCES: {
    titleKey: 'footer.sections.resources',
    links: [{ labelKey: 'footer.links.faqs', path: ROUTES.FAQ }],
  },
} as const

const FOOTER_CONTACT = {
  email: 'contact@ebim.ai',
} as const

export function Footer() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()

  return (
    <footer className="bg-surface-dark text-white">
      <div className="mx-auto max-w-360 px-4 py-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
          <div className="flex flex-col gap-4">
            <Logo variant="light" />
            <a
              href={`mailto:${FOOTER_CONTACT.email}`}
              className="flex items-center gap-2 text-white transition-opacity hover:opacity-80"
            >
              <Mail className="h-5 w-5" />
              <span>{FOOTER_CONTACT.email}</span>
            </a>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-base font-bold">{t(FOOTER_SECTIONS.ENTREPRISE.titleKey)}</h3>
            <nav className="flex flex-col gap-2">
              {FOOTER_SECTIONS.ENTREPRISE.links.map(link => {
                if (isAuthenticated && link.path === ROUTES.SUBSCRIBE) return null
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-base font-normal text-white transition-opacity hover:opacity-80"
                  >
                    {t(link.labelKey)}
                  </Link>
                )
              })}
            </nav>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-base font-bold">{t(FOOTER_SECTIONS.LEGAL.titleKey)}</h3>
            <nav className="flex flex-col gap-2">
              {FOOTER_SECTIONS.LEGAL.links.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-base font-normal text-white transition-opacity hover:opacity-80"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-base font-bold">{t(FOOTER_SECTIONS.RESSOURCES.titleKey)}</h3>
            <nav className="flex flex-col gap-2">
              {FOOTER_SECTIONS.RESSOURCES.links.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-base font-normal text-white transition-opacity hover:opacity-80"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              <a
                href={EXTERNAL_LINKS.HOW_IT_WORKS}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-normal text-white transition-opacity hover:opacity-80"
              >
                {t('footer.links.howItWorks')}
              </a>
            </nav>
          </div>
        </div>
      </div>
      <div className="bg-[#323b4e] py-5 text-center font-normal text-white">
        {t('footer.poweredBy')}
        <a
          href="https://fasfox.com/fr/use-cases/chat-securise-sur-base-documentaire/"
          target="_blank"
          rel="noopener"
          className="underline underline-offset-[4px] transition-opacity hover:opacity-80"
        >
          {t('footer.fasfox')}
        </a>
      </div>
    </footer>
  )
}
